import React, { useState } from 'react';
import { getApiUrl } from '../config';
import { theme, alpha } from '../theme';
import { useResponsive } from '../hooks/useResponsive';

interface Props {
  groupId: string;
  groupName: string;
  onClose: () => void;
  onSuccess: () => void;
  showNotification: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void;
}

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const PLAYERS_OPTIONS = [5, 6, 7, 8, 9, 10, 11];

const inputStyle = (isMobile: boolean): React.CSSProperties => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '11px 14px',
  borderRadius: 12,
  border: `1.5px solid ${theme.colors.border}`,
  background: theme.colors.background,
  color: theme.colors.text,
  fontSize: 15,
  boxSizing: 'border-box',
  outline: 'none',
});

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: theme.colors.textSecondary,
  marginBottom: 6,
};

const CreateRecurringMatchModal: React.FC<Props> = ({
  groupId,
  groupName,
  onClose,
  onSuccess,
  showNotification,
}) => {
  const { isMobile } = useResponsive();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    dayOfWeek: new Date().getDay(),
    time: '21:00',
    location: '',
    mapsUrl: '',
    maxPlayers: 5,
    price: '',
  });

  const set = (key: keyof typeof form, value: string | number) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showNotification('El nombre de la serie es obligatorio', 'error');
      return;
    }
    if (!form.location.trim()) {
      showNotification('El nombre de la cancha es obligatorio', 'error');
      return;
    }
    if (form.mapsUrl.trim()) {
      try {
        new URL(form.mapsUrl);
      } catch {
        showNotification('El link de Google Maps no es válido', 'error');
        return;
      }
    }
    const parsedPrice = parseFloat(form.price);
    if (!form.price || isNaN(parsedPrice) || parsedPrice < 0) {
      showNotification('El precio no es válido', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl(`/api/groups/${groupId}/recurring-matches`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name.trim(),
          location: form.location.trim(),
          mapsUrl: form.mapsUrl.trim(),
          time: form.time,
          dayOfWeek: form.dayOfWeek,
          frequency: 'WEEKLY',
          price: parsedPrice,
          maxPlayers: form.maxPlayers,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showNotification(data.error || 'Error al crear el partido fijo', 'error');
        return;
      }
      const dayName = DAYS[form.dayOfWeek];
      showNotification(
        `Partido fijo creado. El próximo ${dayName} ya está en el grupo.`,
        'success'
      );
      onSuccess();
    } catch {
      showNotification('Error de conexión', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const sheetStyle: React.CSSProperties = {
    background: theme.colors.surface,
    borderRadius: isMobile ? '20px 20px 0 0' : 16,
    maxWidth: 520,
    width: '100%',
    border: `1px solid ${theme.colors.border}`,
    boxShadow: '0 -4px 40px rgba(0,0,0,0.3)',
    maxHeight: '92vh',
    overflowY: 'auto',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 1002,
        padding: isMobile ? 0 : 16,
      }}
      onClick={() => !submitting && onClose()}
      onKeyDown={(e) => { if (e.key === 'Escape' && !submitting) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div style={sheetStyle} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
        {/* Handle (mobile) */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: theme.colors.border }} />
          </div>
        )}

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${theme.colors.border}`,
        }}>
          <button
            type="button"
            onClick={() => !submitting && onClose()}
            style={{ background: 'none', border: 'none', color: theme.colors.textSecondary, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 0 }}
            aria-label="Cerrar"
          >
            ✕
          </button>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.colors.text }}>
            Nuevo partido fijo
          </h3>
          <div style={{ width: 24 }} />
        </div>

        {/* Chip de grupo */}
        <div style={{ padding: '12px 20px 0' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 20,
            background: 'rgba(0,230,118,0.1)',
            border: `1px solid ${alpha('primary', '50')}`,
            fontSize: 13,
            color: theme.colors.primary,
            fontWeight: 600,
          }}>
            ⚽ {groupName}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Nombre */}
          <div>
            <label htmlFor="rm-name" style={labelStyle}>Nombre de la serie *</label>
            <input
              id="rm-name"
              type="text"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="Ej: Martes Fulbito"
              required
              style={{
                ...inputStyle(isMobile),
                borderColor: form.name ? theme.colors.primary : theme.colors.border,
              }}
            />
          </div>

          {/* Día de la semana */}
          <div>
            <label style={labelStyle}>Día de la semana *</label>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => set('dayOfWeek', i)}
                  style={{
                    flex: 1,
                    height: 44,
                    borderRadius: 10,
                    border: form.dayOfWeek === i ? 'none' : `1.5px solid ${theme.colors.border}`,
                    background: form.dayOfWeek === i ? theme.colors.primary : 'transparent',
                    color: form.dayOfWeek === i ? theme.colors.background : theme.colors.textSecondary,
                    fontSize: 12,
                    fontWeight: form.dayOfWeek === i ? 700 : 500,
                    cursor: 'pointer',
                  }}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          {/* Horario + Jugadores por equipo */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="rm-time" style={labelStyle}>Horario *</label>
              <input
                id="rm-time"
                type="time"
                value={form.time}
                onChange={(e) => set('time', e.target.value)}
                required
                style={inputStyle(isMobile)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="rm-players" style={labelStyle}>Jugadores/equipo *</label>
              <select
                id="rm-players"
                value={form.maxPlayers}
                onChange={(e) => set('maxPlayers', Number(e.target.value))}
                style={{ ...inputStyle(isMobile), cursor: 'pointer' }}
              >
                {PLAYERS_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Cancha */}
          <div>
            <label htmlFor="rm-location" style={labelStyle}>Cancha *</label>
            <input
              id="rm-location"
              type="text"
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="Ej: Cancha El Potrero"
              required
              style={inputStyle(isMobile)}
            />
          </div>

          {/* Link Maps */}
          <div>
            <label htmlFor="rm-maps" style={labelStyle}>Link Google Maps *</label>
            <input
              id="rm-maps"
              type="url"
              value={form.mapsUrl}
              onChange={(e) => set('mapsUrl', e.target.value)}
              placeholder="https://maps.google.com/..."
              required
              style={inputStyle(isMobile)}
            />
          </div>

          {/* Precio */}
          <div>
            <label htmlFor="rm-price" style={labelStyle}>Precio por jugador *</label>
            <input
              id="rm-price"
              type="number"
              value={form.price}
              onChange={(e) => set('price', e.target.value)}
              placeholder="Ej: 2500"
              min="0"
              step="any"
              required
              style={inputStyle(isMobile)}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '15px',
              borderRadius: 14,
              border: 'none',
              background: submitting ? theme.colors.textSecondary : theme.colors.primary,
              color: theme.colors.background,
              fontSize: 15,
              fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: 4,
            }}
          >
            {submitting ? 'Creando...' : '🔄  Crear partido fijo'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateRecurringMatchModal;
